import { assign, fromPromise, setup } from 'xstate';

import { isNewUser } from '../../../helpers/user/isNewUser';
import { loadCurrentUser } from '../../../helpers/user/loadCurrentUser';
import { machine as loginMachine } from '../flows/login/machine/machine';
import { machine as signUpMachine } from '../flows/signUp/machine/machine';

import { context as initialContext } from './data/context';
import type { Context, Events, States } from './types';

export const getMachine = (initialState: States) =>
  setup({
    types: {} as { context: Context; events: Events; input: Partial<Context> },
    actions: {
      setUserIsLoggedInTrue: assign(({ context: { user } }) => ({
        user: { ...user, isLoggedIn: true },
      })),
      setUserCreatedAt: assign(({ context: { user }, event }) => ({
        user:
          event.type === 'xstate.done.actor.loadCurrentUser'
            ? { ...user, createdAt: event.output.createdAt }
            : user,
      })),
    },
    actors: {
      loadCurrentUser: fromPromise(async () => loadCurrentUser()),
      loginMachine,
      signUpMachine,
    },
    guards: {
      isCurrentUserNew: ({ context: { user } }) => isNewUser(user.createdAt || ''),
      isLoginFinished: ({ event }) =>
        event.type === 'xstate.done.actor.loginService' && event.output.isLoginFinished,
      isSignUpFinished: ({ event }) =>
        event.type === 'xstate.done.actor.signUpService' && event.output.isSignUpFinished,
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QEMCuAXAFmAduglgMbIED2OAssoZvjmAHQA2pUdAxBOY3QG6kBrRizY4AymABOvImADaABgC6iUAAdSsfGRyqQAD0QAWAGwKGZgJxGFJgOyWATI4WOTJgDQgAnogCMJgAcFoFGgX4ArC4RgbaBAL7xXmhYuATEOlQ0dMKsHFz0DHyCuaIS0rJyfipIIBpaOnqGCHYmAMwMEZEmpkZtptZevggAtG3BClF+Cm0zjoGOfgERickY2HhEJPjkWbSFWlA4AKpqnNxFOPxCDIcnauUyhPLKevXaO7q1zT0MbY4RLptOyRCI2OxGIaIEbWBjOEwRJwxPytQKBOyrEApDbpba7aj7Rh3U7nQrFG7Eh5SJ7yapvTQfchNYwmSwWBSWcZGJyWCJ2UJQ0bjBiTRYzOYLJYIzHYtJbTIEnLMUjICB0KAAYVQkkkaWOsCkpJ4VxKytVWp1eoNkkUNXUDMa338bQiDFiLksEKMdhc7kCgoCwSCYSWCgUgX+Rhl6zlGU+eyVLFV6oturw+sNUkkpEkDDUTBIADMcwBbM0QVNWqS2+kNT7M0askWc1wKOxtcbjZwByx+BhGJbAhF2CERDuOaOpTZx-HZQo0MCEAQASULlfT1uXsAAcmAAO7sGu1d6O0DNPwuhi9px+IyOfoKb13wXjcwdwJj71taxGCLuSc4vK8aKvO2BLqu67oBmkhbruB5VHadQOvWToIB2JjsgOfgAjEnK9p4PjGI4RhupYtgok4A5hIkSQgDgpAQHAeiytOeKUCBYC1oyXxntCSx2M2swuO2nYLI4gpjK4V7AmCt53o4nrtABsZsQmhQiHQXGngYiCyX88xyfyziWJY-qEWhGFYW0AS9n4JnWQktEsbiCpzkS+BHKcWkobxCDdG6rbos4KJ2Aot4SYsrp9HYYImVEixBMprGuYS5YptqaZQda3lMqh2GOHCgQ9O2-J+B+3LieZbSWYOI5josbhLElLnAW5DALuBa4ZVWME7vuOU8TpfkogwoURmYXRBO4YUvm2cL9NVMzcgOCxRk5MbJa1qUaeIqCEM8sCwIWqBMANDZROYXSApYJguI+d4RIKd59t+kzttMsy3n+zVAbOqV7mATCEKQJZgAAYrqYDQWdeXzAwF6xBenr3mR6JPb2V6OKFXRhhyIK2DR8RAA */
    id: 'authenticationMachine',
    context: ({ input }) => ({ ...initialContext, ...input }),
    initial: initialState,
    states: {
      login: {
        invoke: {
          id: 'loginService',
          input: ({ context }) => context,
          src: 'loginMachine',
          onDone: [
            {
              actions: ['setUserIsLoggedInTrue'],
              guard: 'isLoginFinished',
              target: 'loginSuccessful',
            },
            { target: 'signUp' },
          ],
        },
      },
      signUp: {
        invoke: {
          id: 'signUpService',
          input: ({ context }) => context,
          src: 'signUpMachine',
          onDone: [
            {
              actions: ['setUserIsLoggedInTrue'],
              guard: 'isSignUpFinished',
              target: 'loadingCurrentUser',
            },
            { target: 'login' },
          ],
        },
      },
      // TODO: find the way to do not fetch current user here since it is being fetched anyway outside of this machine
      loadingCurrentUser: {
        invoke: {
          id: 'loadCurrentUser',
          src: 'loadCurrentUser',
          onDone: { actions: ['setUserCreatedAt'], target: 'checkIfCurrentUserIsNew' },
          onError: { target: 'welcomeFreeUser' }, // in case of error, we assume that user is new to do not loose potential chance of conversion
        },
      },
      checkIfCurrentUserIsNew: {
        always: [
          { guard: 'isCurrentUserNew', target: 'welcomeFreeUser' },
          { target: 'loginSuccessful' },
        ],
      },
      loginSuccessful: { type: 'final' },
      welcomeFreeUser: { type: 'final' },
    },
  });
